import React, { type ReactNode, useCallback, useMemo, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css'
import { Box, Grid, Text } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message';
import { RecaptchaTermsAndPolicy } from '@atlassian/google-recaptcha';

import { useAnalytics } from '../../../common/ui/analytics';

import { ActionButton } from './action-button';
import messages from './messages';
import { type InvitePanelProps, ProductDisplayNameMap } from './types';

const styles = cssMap({
	grid: {
		gridTemplateAreas: `
			"title invite"
			"info invite"
		`,
	},
	title: {
		gridArea: 'title',
	},
	invite: {
		gridArea: 'invite',
		justifySelf: 'end',
	},
	info: {
		gridArea: 'info',
	},
});

export const InvitePanel = ({
	emails,
	product,
	onInvite,
	onDismiss,
	isRecaptchaLoaded,
	integration,
}: InvitePanelProps): React.JSX.Element | null => {
	const [isLoading, setIsLoading] = useState(false);
	const fireAnalyticsEvent = useAnalytics();

	const attributes = useMemo(
		() => ({
			users: emails.length,
			integration,
		}),
		[emails.length, integration],
	);

	const handleInvite = useCallback(async () => {
		setIsLoading(true);
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'inlineInvite',
			attributes,
		});
		await onInvite();
		setIsLoading(false);
	}, [fireAnalyticsEvent, attributes, onInvite]);

	const handleDismiss = useCallback(() => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'inlineInviteDismissed',
			attributes,
		});
		onDismiss();
	}, [fireAnalyticsEvent, attributes, onDismiss]);

	if (emails.length === 0) {
		return null;
	}

	return (
		<SectionMessage appearance="information">
			<Grid xcss={styles.grid} columnGap="space.100" rowGap="space.025">
				<Box xcss={styles.title}>
					<Text size="medium">
						<FormattedMessage
							{...messages.title}
							values={{
								email: emails[0],
								product: ProductDisplayNameMap[product],
								count: emails.length,
								others: emails.length - 1,
								bold: (chunks: ReactNode[]) => <Text weight="bold">{chunks}</Text>,
							}}
						/>
					</Text>
				</Box>
				<Box xcss={styles.invite}>
					<Button
						appearance="primary"
						spacing="compact"
						isLoading={isLoading || !isRecaptchaLoaded}
						onClick={handleInvite}
						onKeyDown={(event) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.stopPropagation();
							}
						}}
					>
						<FormattedMessage {...messages.invite} />
					</Button>
				</Box>
				<Box xcss={styles.info}>
					{isLoading ? (
						<Text size="small" color="color.text.subtle">
							<RecaptchaTermsAndPolicy />
						</Text>
					) : (
						<Text size="small" color="color.text.subtle">
							<FormattedMessage
								{...messages.skip}
								values={{
									action: (chunks: ReactNode[]) => (
										<ActionButton onClick={handleDismiss}>{chunks}</ActionButton>
									),
								}}
							/>
						</Text>
					)}
				</Box>
			</Grid>
		</SectionMessage >
	);
};
